import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Container,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  alpha
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Description as DocsIcon,
  Psychology as ProjectManagementIcon,
  Extension as IntegrationsIcon,
  RocketLaunch as GettingStartedIcon,
  Compare as CompareIcon,
  AutoStories as GuideIcon
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { API_CONFIG } from '../../config/apiConfig';

const API_BASE = API_CONFIG.TODO_SERVICE_URL;

interface DocumentationSection {
  id: string;
  title: string;
  description: string;
  path: string;
  icon?: string;
  subsections?: DocumentationSection[];
  featured?: boolean;
}

interface DocumentationIndex {
  title: string;
  description: string;
  sections: DocumentationSection[];
}

const DocsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { section, subsection, item } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [docsData, setDocsData] = useState<any>(null);
  const [breadcrumb, setBreadcrumb] = useState<Array<{ title: string; path: string }>>([]);

  const fetchDocumentation = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let endpoint = '/api/v1/docs';

      if (section && subsection && item) {
        endpoint = `/api/v1/docs/${section}/${subsection}/${item}`;
      } else if (section && subsection) {
        endpoint = `/api/v1/docs/${section}/${subsection}`;
      } else if (section) {
        endpoint = `/api/v1/docs/${section}`;
      }

      const response = await fetch(`${API_BASE}${endpoint}`);

      if (!response.ok) {
        throw new Error('Documentation not found');
      }

      const result = await response.json();
      if (result.success) {
        setDocsData(result.data);
        setBreadcrumb(result.data.breadcrumb || []);
      } else {
        throw new Error(result.error || 'Failed to fetch documentation');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load documentation');
      console.error('Error fetching documentation:', err);
    } finally {
      setLoading(false);
    }
  }, [section, subsection, item]);

  useEffect(() => {
    fetchDocumentation();
  }, [fetchDocumentation]);

  const getIcon = (iconName: string) => {
    const icons: Record<string, React.ReactNode> = {
      'project-management': <ProjectManagementIcon />,
      'getting-started': <GettingStartedIcon />,
      'integrations': <IntegrationsIcon />
    };
    return icons[iconName] || <DocsIcon />;
  };

  const renderBreadcrumbs = () => {
    if (!breadcrumb || breadcrumb.length === 0) return null;

    return (
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        style={{ marginBottom: 24 }}
      >
        {breadcrumb.map((crumb, index) => {
          const isLast = index === breadcrumb.length - 1;
          return isLast ? (
            <Typography key={index} color="text.primary" variant="body2">
              {crumb.title}
            </Typography>
          ) : (
            <Link
              key={index}
              component={RouterLink}
              to={crumb.path}
              variant="body2"
              underline="hover"
              color="inherit"
            >
              {crumb.title}
            </Link>
          );
        })}
      </Breadcrumbs>
    );
  };

  const renderDocumentationIndex = (data: DocumentationIndex) => (
    <div>
      <Typography variant="h3" gutterBottom style={{ fontWeight: 700 }}>
        {data.title}
      </Typography>
      <Typography variant="h6" color="text.secondary" paragraph style={{ marginBottom: 32 }}>
        {data.description}
      </Typography>

      <Grid container spacing={3}>
        {data.sections.map((section) => (
          <Grid item xs={12} md={6} lg={4} key={section.id}>
            <Card
              style={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onClick={() => navigate(section.path)}
            >
              <CardContent style={{ padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                  <div
                    style={{
                      padding: 8,
                      borderRadius: 8,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      marginRight: 16,
                    }}
                  >
                    {getIcon(section.icon || 'default')}
                  </div>
                  <Typography variant="h6" style={{ fontWeight: 600 }}>
                    {section.title}
                  </Typography>
                </div>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {section.description}
                </Typography>
                {section.subsections && (
                  <div>
                    <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600 }}>
                      Includes:
                    </Typography>
                    {section.subsections.slice(0, 2).map((sub) => (
                      <Chip
                        key={sub.id}
                        label={sub.title}
                        size="small"
                        variant="outlined"
                        style={{ marginRight: 8, marginBottom: 8 }}
                      />
                    ))}
                    {section.subsections.length > 2 && (
                      <Chip
                        label={`+${section.subsections.length - 2} more`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );

  const renderSection = (data: any) => (
    <div>
      <Typography variant="h3" gutterBottom style={{ fontWeight: 700 }}>
        {data.title}
      </Typography>
      <Typography variant="h6" color="text.secondary" paragraph style={{ marginBottom: 32 }}>
        {data.description}
      </Typography>

      <Grid container spacing={3}>
        {data.subsections?.map((subsection: any) => (
          <Grid item xs={12} md={subsection.featured ? 12 : 6} key={subsection.id}>
            <Card
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: subsection.featured ? '2px solid #325BA8' : '1px solid rgba(0,0,0,0.12)',
              }}
              onClick={() => navigate(subsection.path)}
            >
              <CardContent style={{ padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                  <Typography variant="h5" style={{ fontWeight: 600 }}>
                    {subsection.title}
                  </Typography>
                  {subsection.featured && (
                    <Chip
                      label="Featured"
                      color="primary"
                      size="small"
                      style={{ marginLeft: 16 }}
                    />
                  )}
                </div>
                <Typography variant="body1" color="text.secondary">
                  {subsection.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );

  const renderMethodologies = (data: any) => (
    <div>
      <Typography variant="h3" gutterBottom style={{ fontWeight: 700 }}>
        {data.title}
      </Typography>
      <Typography variant="h6" color="text.secondary" paragraph style={{ marginBottom: 32 }}>
        {data.description}
      </Typography>

      {/* Quick Actions */}
      <Paper style={{ padding: 24, marginBottom: 32, backgroundColor: alpha(theme.palette.primary.main, 0.02) }}>
        <Typography variant="h6" gutterBottom style={{ fontWeight: 600 }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          {data.quickActions?.map((action: any) => (
            <Grid item xs={12} sm={6} key={action.type}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={action.type === 'comparison' ? <CompareIcon /> : <GuideIcon />}
                onClick={() => navigate(action.path)}
                style={{ justifyContent: 'flex-start', padding: 16 }}
              >
                <div style={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </div>
              </Button>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Overview */}
      {data.overview && (
        <Paper style={{ padding: 24, marginBottom: 32 }}>
          <Typography variant="h6" gutterBottom style={{ fontWeight: 600 }}>
            Overview
          </Typography>
          <Typography variant="body1" paragraph>
            {data.overview.summary}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Total Methodologies: {data.overview.totalMethodologies}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                Categories:
              </Typography>
              <div>
                {data.overview.categories.map((category: string) => (
                  <Chip
                    key={category}
                    label={category}
                    size="small"
                    variant="outlined"
                    style={{ marginRight: 8, marginBottom: 8 }}
                  />
                ))}
              </div>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Methodologies Grid */}
      <Typography variant="h5" gutterBottom style={{ fontWeight: 600, marginBottom: 24 }}>
        All Methodologies
      </Typography>
      <Grid container spacing={3}>
        {data.methodologies?.map((methodology: any) => (
          <Grid item xs={12} md={6} lg={4} key={methodology.id}>
            <Card
              style={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onClick={() => navigate(methodology.path)}
            >
              <CardContent style={{ padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <Typography variant="h6" style={{ fontWeight: 600 }}>
                    {methodology.title}
                  </Typography>
                  <Chip
                    label={methodology.complexity}
                    size="small"
                    color={
                      methodology.complexity === 'Simple' ? 'success' :
                      methodology.complexity === 'Moderate' ? 'warning' : 'error'
                    }
                    variant="outlined"
                  />
                </div>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {methodology.description}
                </Typography>
                <Chip
                  label={methodology.category}
                  size="small"
                  style={{ marginBottom: 16 }}
                />
                <div>
                  <Typography variant="subtitle2" gutterBottom>
                    Best For:
                  </Typography>
                  {methodology.bestFor?.map((item: string, index: number) => (
                    <Chip
                      key={index}
                      label={item}
                      size="small"
                      variant="outlined"
                      style={{ marginRight: 4, marginBottom: 4 }}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );

  const renderContent = () => {
    if (!docsData) return null;

    // Documentation index
    if (docsData.sections) {
      return renderDocumentationIndex(docsData);
    }

    // Section page (like project-management)
    if (docsData.subsections) {
      return renderSection(docsData);
    }

    // Methodologies overview page
    if (docsData.methodologies) {
      return renderMethodologies(docsData);
    }

    // Specific methodology or other content pages
    return (
      <div>
        <Typography variant="h3" gutterBottom style={{ fontWeight: 700 }}>
          {docsData.title}
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          {docsData.description}
        </Typography>
        {/* Add more specific content rendering as needed */}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
      <Container maxWidth="lg" style={{ paddingTop: 32, paddingBottom: 32 }}>
        {renderBreadcrumbs()}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 64, paddingBottom: 64 }}>
            <CircularProgress />
          </div>
        ) : error ? (
          <Alert severity="error" style={{ marginBottom: 24 }}>
            {error}
          </Alert>
        ) : (
          renderContent()
        )}
      </Container>
    </div>
  );
};

export default DocsPage;
